import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';

function Terms() {
  return (
    <div className="min-h-screen bg-white font-quicksand">
      <SEO
        title="Terms of Use"
        description="Terms of use for StudyPass RDH, the NBDHE dental hygiene board exam prep platform."
        path="/terms"
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-study-pink to-study-teal py-12 px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-bold text-study-black mb-2">
          Terms of Use
        </h1>
        <p className="text-study-black">Last updated: January 2025</p>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg max-w-none">
          <p className="text-study-black text-lg mb-6">
            StudyPassRDH offers a personalized subscription service enabling members to access
            educational content ("StudyPassRDH content") via the internet on compatible devices
            ("StudyPassRDH ready devices").
          </p>

          <p className="text-study-black mb-8">
            Provided by Study Pass LLC, you agree to these Terms of Use governing your use of our
            service. "StudyPassRDH service," "our service," or "the service" herein refer to the
            personalized service facilitating the discovery and access of StudyPassRDH content,
            including all associated features, functionalities, recommendations, reviews, websites,
            user interfaces, and software.
          </p>

          {/* Section 1 */}
          <Section number="1" title="Membership">
            <SubSection number="1.1">
              To access the service, you need internet connectivity, StudyPassRDH ready devices,
              and one or more Payment Methods. You must cancel before renewal to avoid fees.
            </SubSection>

            <SubSection number="1.2">
              Various subscription plans are available, with details disclosed at signup or via
              other communications.
            </SubSection>

            <SubSection number="1.3.1" title="StudyPassRDH Bronze Package">
              Membership is continuous and auto-renews unless terminated. To access the service,
              you need internet connectivity, StudyPassRDH ready devices, and one or more Payment
              Methods. You must cancel before renewal to avoid fees. The StudyPass Bronze Monthly
              Plan is the only plan on auto-renew.
            </SubSection>

            <SubSection number="1.3.2" title="StudyPassRDH Silver Package">
              Semester-based subscription billed at the start of each semester. Subscription can
              be canceled within 24 hours of payment for a full refund; after 24 hours, fees are
              non-refundable.
            </SubSection>

            <SubSection number="1.3.3" title="StudyPassRDH Gold Package">
              Annual subscription billed annually. Subscription can be canceled within 24 hours
              of payment for a full refund; after 24 hours, fees are non-refundable.
            </SubSection>
          </Section>

          {/* Section 2 */}
          <Section number="2" title="Billing and Cancellation">
            <p className="text-study-black mb-4">
              Billing occurs on specified dates, with fees and additional charges billed to your
              Payment Method. Billing cycle duration depends on your chosen subscription plan.
              Visit the website for billing details.
            </p>

            <SubSection number="2.1">
              Ensure a valid Payment Method is provided. We reserve the right to suspend access
              for failed payments.
            </SubSection>

            <SubSection number="2.2">
              You authorize us to charge any associated Payment Method if the primary one fails.
            </SubSection>
          </Section>

          {/* Section 3 */}
          <Section number="3" title="StudyPassRDH Service">
            <p className="text-study-black mb-4">
              To join, you must be at least 18 years old. Minors require parental or guardian involvement.
            </p>

            <SubSection number="3.1">
              Content access is for personal, non-commercial use only. A limited, non-exclusive,
              non-transferable right to access StudyPassRDH content is granted during membership.
              No other rights are conferred. Public performance using the service is prohibited.
            </SubSection>
          </Section>

          {/* Section 4 */}
          <Section number="4" title="Authorization of Charges">
            <p className="text-study-black mb-4">
              By creating an account and providing your Payment Method, you authorize StudyPassRDH
              to charge subscription fees and other associated costs.
            </p>

            <SubSection number="4.1">
              StudyPassRDH requires your express consent for changes to fees or billing terms.
            </SubSection>

            <SubSection number="4.2">
              Maintain a current and valid Payment Method. Notify StudyPassRDH of any changes promptly.
            </SubSection>

            <SubSection number="4.3">
              Failure to maintain a valid Payment Method may result in account suspension or termination.
            </SubSection>
          </Section>
        </div>

        {/* Back Link */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/"
            className="text-study-teal font-bold hover:text-study-pink transition-all"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-2xl font-bold text-study-black mb-4">
        {number}. {title}
      </h2>
      {children}
    </div>
  );
}

function SubSection({ number, title, children }) {
  return (
    <div className="mb-4 pl-4 border-l-2 border-study-teal">
      <p className="text-study-black">
        <span className="font-semibold">{number}</span>
        {title && <span className="font-semibold"> {title}:</span>}{' '}
        {children}
      </p>
    </div>
  );
}

export default Terms;
